import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Proposal } from "../types";
import { fetchProposalByEmail } from "../services/documentService";
import { transformPDFUrl } from "../utils/pdfUtils";
import { apiUrl, isStagingLink } from "../config/apiBase";
import ProposalActions from "../components/ProposalActions";
import SuccessMessage from "../components/SuccessMessage";
import NotFound from "../components/NotFound";

const DocumentView: React.FC = () => {
  const { id, encryptedId } = useParams<{
    id?: string;
    encryptedId?: string;
  }>();
  const location = useLocation();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [responseSubmitted, setResponseSubmitted] = useState(false);
  const [submittedAction, setSubmittedAction] = useState<
    "approve" | "decline" | null
  >(null);
  const [submittedReason, setSubmittedReason] = useState<string | undefined>();

  // Get encrypted_id from route parameters
  const searchParams = new URLSearchParams(location.search);
  const queryEncryptedId = searchParams.get("encrypted_id");

  const handleSuccess = (action: "approve" | "decline", reason?: string) => {
    setSubmittedAction(action);
    setSubmittedReason(reason);
    setResponseSubmitted(true);

    // Update proposal status
    if (proposal) {
      setProposal({
        ...proposal,
        status: action === "approve" ? "approved" : "declined",
        reason: reason,
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      // Use encryptedId from route params, or queryEncryptedId from query params, or regular id as encrypted token
      const identifier = encryptedId || queryEncryptedId || id;
      console.log("=== DEBUG: DocumentView useEffect ===");
      console.log("Current URL:", window.location.href);
      console.log("Using identifier:", identifier);
      console.log("Route params - id:", id, "encryptedId:", encryptedId);
      console.log("Query params - encrypted_id:", queryEncryptedId);
      console.log("Sent from staging:", isStagingLink());
      console.log(
        "API URL will be:",
        `${apiUrl()}/proposal/email/show?encrypted_id=${identifier}`,
      );

      if (!identifier) {
        console.log("No valid identifier found");
        setError(true);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        let proposalData: Proposal | null = null;

        // Use email-based API for encrypted tokens (which include special characters like encrypted JSON)
        console.log("Fetching proposal with encrypted_id:", identifier);
        proposalData = await fetchProposalByEmail(identifier);

        if (proposalData) {
          setProposal(proposalData);
          // Set page title with proposal ID
          document.title = `Proposal #${proposalData.id} - Cinch`;

          // Check if already responded
          if (
            proposalData.status === "approved" ||
            proposalData.status === "declined"
          ) {
            setResponseSubmitted(true);
            setSubmittedAction(
              proposalData.status === "approved" ? "approve" : "decline",
            );
            setSubmittedReason(proposalData.reason);
          }
        } else {
          console.log("No proposal data received from API");
          setError(true);
          document.title = "Proposal Not Found - Cinch";
        }
      } catch (err) {
        console.error("Error fetching proposal:", err);
        setError(true);
        document.title = "Error - Cinch";
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [encryptedId, queryEncryptedId, id]);

  const canRespond = proposal?.status === "pending" && !responseSubmitted;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cinch-light to-white flex flex-col items-center justify-center px-4">
        <div className="text-center animate-fade-in">
          <img
            src="https://cleancinch.com/wp-content/uploads/2025/05/aa836ec1-54ef-4339-9b94-8b6eec324faa.png"
            alt="Cinch Logo"
            className="h-16 w-auto mx-auto mb-8"
          />
          <div className="relative mb-4">
            <div className="animate-spin-slow w-12 h-12 border-4 border-cinch-light border-t-cinch-primary rounded-full mx-auto"></div>
          </div>
          <h2 className="text-2xl font-semibold text-cinch-primary mb-2">
            Loading your proposal...
          </h2>
          <p className="text-gray-600">
            Please wait while we retrieve your document
          </p>
        </div>
      </div>
    );
  }

  if (error || !proposal) {
    return <NotFound />;
  }

  if (responseSubmitted && submittedAction) {
    const proposalTitle =
      proposal.display_title ||
      proposal.data?.templateTitle ||
      proposal.customer ||
      "Proposal";
    return (
      <SuccessMessage
        action={submittedAction}
        reason={submittedReason}
        proposalTitle={proposalTitle}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cinch-light to-white">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 sm:py-6">
            <div className="flex items-center min-w-0">
              <img
                src="https://cleancinch.com/wp-content/uploads/2025/05/aa836ec1-54ef-4339-9b94-8b6eec324faa.png"
                alt="Cinch Logo"
                className="h-8 sm:h-10 w-auto mr-3 sm:mr-4 flex-shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-cinch-primary truncate">
                  Proposal #{proposal.id}
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">
                  {proposal.status === "pending"
                    ? "Please review and respond"
                    : `Status: ${proposal.status}`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* On mobile the actions/details come first -matches the
              page's real priority (review and respond), and avoids
              forcing anyone to scroll past a full-height PDF preview
              just to find the Approve/Decline buttons. Desktop keeps
              the original PDF-left, sidebar-right layout. */}
          <div className="order-2 lg:order-1 lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 sm:p-4">
              <h3 className="text-base sm:text-lg font-semibold text-cinch-primary mb-3 sm:mb-4">
                Proposal Document
              </h3>
              <div className="flex justify-center">
                <div className="w-full max-w-2xl">
                  <iframe
                    src={transformPDFUrl(proposal.pdf_url)}
                    className="w-full aspect-[1/1.414] min-h-[380px] sm:min-h-[560px] lg:min-h-[800px] border border-gray-300 rounded-lg"
                    title="Proposal PDF"
                    scrolling="no"
                    style={{ overflow: "hidden" }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 lg:col-span-1 space-y-4 sm:space-y-6">
            {canRespond && (id || encryptedId || queryEncryptedId) && (
              <ProposalActions
                secretCode={!encryptedId && !queryEncryptedId ? id : undefined}
                encryptedId={encryptedId || queryEncryptedId || id}
                onSuccess={handleSuccess}
                disabled={!canRespond}
              />
            )}

            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 sm:p-6">
              <h2 className="text-xl font-semibold text-cinch-primary mb-4">
                Proposal Details
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-gray-900 font-medium">
                    {proposal.display_title ||
                      proposal.data?.templateTitle ||
                      proposal.customer ||
                      "Proposal"}
                  </p>
                </div>
                <div>
                  <div
                    className="text-gray-600 text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: proposal.data.templateDescription,
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer
                  </label>
                  <p className="text-gray-900">{proposal.customer}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Category
                  </label>
                  <p className="text-gray-600 text-sm capitalize">
                    {proposal.type}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Created
                  </label>
                  <p className="text-gray-600 text-sm">
                    {new Date(proposal.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      proposal.status === "approved"
                        ? "bg-green-100 text-green-800"
                        : proposal.status === "declined"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {proposal.status.charAt(0).toUpperCase() +
                      proposal.status.slice(1)}
                  </span>
                </div>
                {proposal.data.residentialPrice &&
                  proposal.data.residentialPrice.total > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total Amount
                      </label>
                      <p className="text-gray-900 font-medium">
                        ${proposal.data.residentialPrice.total}
                      </p>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentView;
